/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState, useMemo, useCallback } from 'react'
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'
import R4RIcon from 'components/R4RIcon'
import {
  useLeaseContractsList,
  LeaseContractResponse
} from 'services/contracts/hooks/useLeaseContracts'
import { useSchedulesWithDocumnets } from 'services/contracts/hooks/useSchedulesWithDocumnets'
import { useNavigate } from 'react-router-dom'

interface LeaseContractWithBalance extends LeaseContractResponse {
  balance?: number
  hasUnpaidBalance?: boolean
}

const WidgetBalance: FC = () => {
  const navigate = useNavigate()
  const { formatMessage, formatNumber } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { leaseContractsList } = useLeaseContractsList()

  const allLeases = leaseContractsList || []

  const itemWithHighestId =
    allLeases.length > 0
      ? allLeases.reduce((max, item) => {
          return item.id > max.id ? item : max
        }, allLeases[0])
      : null

  // Umowa, dla której sprawdzamy saldo w danym momencie
  const [currentCheckLease, setCurrentCheckLease] = useState<number | null>(null)
  const [isCheckingBalance, setIsCheckingBalance] = useState(false)
  const [checkedLeases, setCheckedLeases] = useState<number[]>([])
  const [contractsBalances, setContractsBalances] = useState<{ [key: number]: number }>({})

  const { accountBalance, isLoading } = useSchedulesWithDocumnets({
    activeLeaseContractId: currentCheckLease || 0,
    isPaid: false
  })

  const checkNextLease = useCallback(() => {
    if (!allLeases.length) return

    const uncheckedLease = allLeases.find((lease) => !checkedLeases.includes(lease.id))

    if (uncheckedLease) {
      setCurrentCheckLease(uncheckedLease.id)
      setIsCheckingBalance(true)
    } else {
      setIsCheckingBalance(false)
    }
  }, [allLeases, checkedLeases])

  useEffect(() => {
    if (currentCheckLease && !isLoading && isCheckingBalance) {
      setContractsBalances((prev) => ({
        ...prev,
        [currentCheckLease]: accountBalance
      }))
      setCheckedLeases((prev) => [...prev, currentCheckLease])
      setCurrentCheckLease(null)
      setTimeout(checkNextLease, 10)
    }
  }, [currentCheckLease, isLoading, accountBalance, isCheckingBalance, checkNextLease])

  useEffect(() => {
    if (allLeases.length > 0 && !isCheckingBalance && checkedLeases.length === 0) {
      checkNextLease()
    }
  }, [allLeases, isCheckingBalance, checkedLeases, checkNextLease])

  const totalBalance = useMemo(() => {
    return Object.values(contractsBalances).reduce((sum, balance) => sum + balance, 0)
  }, [contractsBalances])

  const contractsWithBalances = useMemo<LeaseContractWithBalance[]>(() => {
    return allLeases.map((lease) => {
      const balance = contractsBalances[lease.id] || 0

      return {
        ...lease,
        balance,
        hasUnpaidBalance: balance > 0
      }
    })
  }, [allLeases, contractsBalances])

  const allLeasesChecked = useMemo(() => {
    if (allLeases.length === 0) return true

    return allLeases.every((lease) => checkedLeases.includes(lease.id))
  }, [allLeases, checkedLeases])

  if (!allLeases.length) {
    return null
  }

  // Umowa, której dotyczy saldo: pierwsza z nieopłaconym saldem albo najnowsza
  const balanceLease =
    contractsWithBalances.find(({ hasUnpaidBalance }) => hasUnpaidBalance) || itemWithHighestId

  const isBalanceLeaseFrozen = allLeases.find(
    (lease) => lease.id === balanceLease?.id
  )?.isOperationsFrozen

  const balanceLabel =
    totalBalance === 0
      ? formatMessage({
          id: 'balanceWidget.paid',
          defaultMessage: 'Opłacone'
        })
      : `-${formatNumber(totalBalance, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} PLN`

  const showInvoicesButton = !!balanceLease && !isBalanceLeaseFrozen

  const goToInvoices = () =>
    navigate(`additional-services/${balanceLease?.id}/payment-schedule`)

  if (isDesktop) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '36px 40px',
          background: theme.r4r.gradient,
          boxShadow: theme.r4r.heroShadow
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{
              color: '#fff',
              opacity: 0.55,
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
            {formatMessage({
              id: 'balanceWidget.yourBalance',
              defaultMessage: 'Twoje saldo'
            })}
          </Typography>
          {!allLeasesChecked ? (
            <CircularProgress size={44} sx={{ color: '#fff' }} />
          ) : (
            <Typography
              sx={{ color: '#fff', fontSize: '44px', fontWeight: 800, lineHeight: 'normal' }}>
              {balanceLabel}
            </Typography>
          )}
        </Box>
        {showInvoicesButton && (
          <Button
            onClick={goToInvoices}
            startIcon={<R4RIcon name='receipt' size={18} />}
            sx={{
              backgroundColor: theme.r4r.yellow,
              color: theme.r4r.navy,
              fontSize: '15px',
              fontWeight: 700,
              padding: '14px 28px',
              borderRadius: 0,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#e5b500' }
            }}>
            {formatMessage({
              id: 'balanceWidget.goToInvoices',
              defaultMessage: 'Przejdź do faktur'
            })}
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        padding: '24px 20px 20px',
        background: theme.r4r.gradient,
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.55)',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.8px',
          textTransform: 'uppercase'
        }}>
        {formatMessage({
          id: 'balanceWidget.yourBalance',
          defaultMessage: 'Twoje saldo'
        })}
      </Typography>
      {!allLeasesChecked ? (
        <CircularProgress size={36} sx={{ color: '#fff', my: '8px' }} />
      ) : (
        <Typography
          sx={{ color: '#fff', fontSize: '32px', fontWeight: 800, lineHeight: 'normal', mt: '4px' }}>
          {balanceLabel}
        </Typography>
      )}
      {!!balanceLease && (
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', mt: '4px' }}>
          {formatMessage(
            {
              id: 'balanceWidget.appliesToContract',
              defaultMessage: 'Dotyczy umowy {contractNumber}'
            },
            { contractNumber: balanceLease.contractNumber }
          )}
        </Typography>
      )}
      {showInvoicesButton && (
        <Button
          onClick={goToInvoices}
          fullWidth
          startIcon={<R4RIcon name='receipt' size={18} />}
          sx={{
            mt: '16px',
            backgroundColor: theme.r4r.yellow,
            color: theme.r4r.navy,
            fontSize: '13px',
            fontWeight: 700,
            padding: '5px 12px',
            minHeight: '28px',
            borderRadius: 0,
            '&:hover': { backgroundColor: '#e5b500' }
          }}>
          {formatMessage({
            id: 'balanceWidget.goToInvoices',
            defaultMessage: 'Przejdź do faktur'
          })}
        </Button>
      )}
    </Box>
  )
}

export default WidgetBalance
